import React from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Brand } from '@daedalus/core/src/brand/types';
import { checkIsBrandVio, getLocalizedContactUrl, getLocalizedHelpUrl } from '@daedalus/core/src/brand/utils';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { useDeviceLayout } from '../../../context/deviceLayout';
import { Divider } from '../Divider';
import { Text } from '../Text';
import { FooterLink } from './FooterLinkButton';
import { SocialMediaLinksWidget } from './SocialMediaLinksWidget';
type ForcedLayout = 'compact' | 'wide';
export interface Props {
  /** Brand configuration. It can be one of our brands or a white-label brand. Determines which links and information to display. */
  brand: Brand;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Which layout to force. If not set, the component will use the device layout to determine which layout to display. */
  forcedLayout?: ForcedLayout;
  /** The component to render in the widget slot (e.g. Trustpilot) */
  widget?: React.ReactNode;
  /** The component to render in the getTheApp slot */
  getTheAppSlot?: React.ReactNode;
  /** The component to render in the cookie preference link slot */
  CookiePreferencesLinkSlot?: React.ReactNode;
  /** The language code to use for the footer links */
  languageCode?: string;
}
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1a07ymc",
  propsAsIs: false
});
export const FooterWrapper = /*#__PURE__*/styled('div')({
  name: "FooterWrapper",
  class: "f1yss1xj",
  propsAsIs: false
});
const _exp9 = /*#__PURE__*/() => Text;
export const FooterCopyright = /*#__PURE__*/styled(_exp9())({
  name: "FooterCopyright",
  class: "fhid62y",
  propsAsIs: true
});
export const FooterNav = /*#__PURE__*/styled('nav')({
  name: "FooterNav",
  class: "f17k6bho",
  propsAsIs: false
});
const _exp13 = /*#__PURE__*/() => ({
  layoutIsCompact
}) => layoutIsCompact ? 'center' : 'flex-start';
export const FooterNavList = /*#__PURE__*/styled('ul')({
  name: "FooterNavList",
  class: "f1lhzr4c",
  propsAsIs: false,
  vars: {
    "f1lhzr4c-0": [_exp13()]
  }
});
export const FooterWidgetsWrapper = /*#__PURE__*/styled('div')({
  name: "FooterWidgetsWrapper",
  class: "f2ryydh",
  propsAsIs: false
});
export const CenterDividerWrapper = /*#__PURE__*/styled('div')({
  name: "CenterDividerWrapper",
  class: "c9pxl20",
  propsAsIs: false
});
export const Footer = ({
  brand,
  widget,
  className,
  forcedLayout,
  getTheAppSlot,
  CookiePreferencesLinkSlot,
  languageCode
}: Props) => {
  const {
    isDesktopMd,
    isDesktopLg,
    isDesktop
  } = useDeviceLayout();
  const layoutIsCompact = forcedLayout === undefined ? !(isDesktopMd || isDesktopLg) : forcedLayout === 'compact';
  const brandIsVio = checkIsBrandVio(brand);
  const shouldRenderWidgetsWrapper = !!widget || brandIsVio;
  const currentYear = new Date().getFullYear();
  return <Wrapper className={className}>
      <Divider />
      <FooterWrapper className={cx(layoutIsCompact ? '--compact' : '--wide')}>
        {shouldRenderWidgetsWrapper && <FooterWidgetsWrapper>
            {widget}
            {brandIsVio && !layoutIsCompact && <SocialMediaLinksWidget brand={brand} />}
          </FooterWidgetsWrapper>}
        {brandIsVio && getTheAppSlot && !isDesktop && getTheAppSlot}

        {widget && layoutIsCompact && <CenterDividerWrapper>
            <Divider />
          </CenterDividerWrapper>}

        <FooterNav>
          <FooterNavList layoutIsCompact={layoutIsCompact}>
            <li>
              <FooterCopyright variant="bodyXS">
                © {currentYear} {brand.name}
              </FooterCopyright>
            </li>
            {brand.links.aboutUsUrl && <li>
                <FooterLink href={brand.links.aboutUsUrl} target="_blank">
                  <FormattedMessageWrapper dataId="AboutUs" id="about" defaultMessage="About" />
                </FooterLink>
              </li>}
            {brand.links.careersUrl && <li>
                <FooterLink href={brand.links.careersUrl} target="_blank">
                  <FormattedMessageWrapper dataId="Careers" id="careers" defaultMessage="Careers" />
                </FooterLink>
              </li>}
            {brand.links.developersUrl && <li>
                <FooterLink href={brand.links.developersUrl} target="_blank">
                  <FormattedMessageWrapper dataId="Affiliates" id="affiliates" defaultMessage="Affiliates" />
                </FooterLink>
              </li>}
            {brand.links.blogUrl && <li>
                <FooterLink href={brand.links.blogUrl} target="_blank">
                  <FormattedMessageWrapper dataId="Blog" id="blog" defaultMessage="Blog" />
                </FooterLink>
              </li>}
            {brand.links.helpUrl && <li>
                <FooterLink href={getLocalizedHelpUrl(brand, languageCode)} target="_blank">
                  <FormattedMessageWrapper dataId="Help" id="help" defaultMessage="Help" />
                </FooterLink>
              </li>}
            {Boolean(CookiePreferencesLinkSlot) && <>{CookiePreferencesLinkSlot}</>}
            {brand.links.privacyPolicyUrl && <li>
                <FooterLink href={brand.links.privacyPolicyUrl} target="_blank">
                  <FormattedMessageWrapper dataId="PrivacyPolicy" id="privacy" defaultMessage="Privacy" />
                </FooterLink>
              </li>}
            {brand.links.termsOfUseUrl && <li>
                <FooterLink href={brand.links.termsOfUseUrl} target="_blank">
                  <FormattedMessageWrapper dataId="TermsOfUse" id="terms" defaultMessage="Terms" />
                </FooterLink>
              </li>}
            {brand.links.contactUrl && <li>
                <FooterLink href={getLocalizedContactUrl(brand, languageCode)} target="_blank">
                  <FormattedMessageWrapper dataId="Contact" id="contact" defaultMessage="Contact" />
                </FooterLink>
              </li>}
            {brandIsVio && isDesktop && getTheAppSlot && <li>{getTheAppSlot}</li>}
            {brandIsVio && layoutIsCompact && <li>
                <SocialMediaLinksWidget brand={brand} />
              </li>}
          </FooterNavList>
        </FooterNav>
      </FooterWrapper>
    </Wrapper>;
};

require("./Footer.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Footer.tsx");